import { Link } from 'react-router-dom';
import { track } from '../lib/track';
import { insuranceLine, signatureLine, PICKUP_ENABLED, SHIP_WINDOW } from '../lib/shippingPolicy';
import { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import HoldNotice from '../components/HoldNotice';
import PendingOrderBanner from '../components/PendingOrderBanner';
import { setPendingOrder } from '../lib/pendingOrder';
import { captureCart } from '../lib/cartLead';

function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

const blankAddress = { name: '', street1: '', street2: '', city: '', state: '', zip: '', country: 'US' };

export default function CheckoutPage() {
  // No clearCart here on purpose — the cart is emptied on the order page once
  // the order actually lands, so an abandoned checkout keeps its cart.
  const { items, subtotalCents } = useCart();

  // Step marker for the funnel. Subtotal rides along so drop-off can be read
  // against basket size, not just counts.
  useEffect(() => { track('checkout_view', { value_cents: subtotalCents }); }, [subtotalCents]);
  const { user } = useAuth();

  const [fulfillment, setFulfillment] = useState(PICKUP_ENABLED ? 'pickup' : 'ship');
  const [guestEmail, setGuestEmail] = useState(user?.email || '');
  const [guestName, setGuestName] = useState('');
  const [address, setAddress] = useState(blankAddress);

  // quote = { options: [...], insurance_cents, signature_required }. selectedRateId
  // is the shippo_rate_id of whichever service the customer picked.
  const [quote, setQuote] = useState(null);
  const [selectedRateId, setSelectedRateId] = useState('');
  const [rateError, setRateError] = useState('');
  const [ratePending, setRatePending] = useState(false);

  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // As soon as a guest has typed a real email, remember the cart against it.
  // If they leave before paying, shinyvault-cart-reminder picks it up.
  // Signed-in shoppers are already mirrored from CartContext.
  useEffect(() => {
    if (user || items.length === 0) return undefined;
    const t = setTimeout(() => captureCart(guestEmail, items, 'checkout'), 1200);
    return () => clearTimeout(t);
  }, [guestEmail, items, user]);

  if (items.length === 0) {
    return (
      <div className="sv-container sv-page-top sv-page-bottom" style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 900 }}>Your cart is empty</h1>
      </div>
    );
  }

  const addressComplete = address.name && address.street1 && address.city && address.state && address.zip;

  const clearQuote = () => { setQuote(null); setSelectedRateId(''); };

  const getRate = async () => {
    setRateError('');
    clearQuote();
    setRatePending(true);
    try {
      const { data, error } = await supabase.functions.invoke('shinyvault-checkout', {
        body: {
          action: 'get_shipping_rate',
          items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
          to_address: address,
        },
      });
      if (error) throw error;
      if (!data.available || !data.options?.length) {
        setRateError(data.reason || 'Shipping is not available for that address.');
      } else {
        setQuote(data);
        // Preselect the cheapest so the customer can just continue.
        const cheapest = data.options.find((o) => o.cheapest) || data.options[0];
        setSelectedRateId(cheapest.shippo_rate_id);
      }
    } catch (e) {
      let message = e.message || 'Could not fetch a shipping rate.';
      if (e.context?.json) {
        try {
          const body = await e.context.json();
          if (body?.error) message = body.error;
        } catch { /* fall back to the generic message */ }
      }
      setRateError(message);
    } finally {
      setRatePending(false);
    }
  };

  const selected = quote?.options?.find((o) => o.shippo_rate_id === selectedRateId) || null;
  const shippingCents = fulfillment === 'ship' && selected ? selected.total_cents : 0;
  const totalCents = subtotalCents + shippingCents;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!user && !guestEmail.trim()) {
      setSubmitError('Enter an email so we can send your order confirmation.');
      return;
    }
    if (fulfillment === 'ship' && !selected) {
      setSubmitError('Choose a shipping option first.');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('shinyvault-checkout', {
        body: {
          action: 'create_checkout_session',
          items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
          fulfillment_method: fulfillment,
          guest_email: guestEmail.trim() || null,
          guest_name: guestName.trim() || null,
          shipping_address: fulfillment === 'ship' ? address : null,
          // Only the chosen rate id is sent. Cost, carrier and service are
          // re-derived from it server-side, so a tampered price can't stick.
          shippo_rate_id: fulfillment === 'ship' ? selected?.shippo_rate_id : null,
        },
      });
      if (error) throw error;
      if (data?.error === 'sold_out') {
        setSubmitError('One of the items in your cart just sold out — please remove it and try again.');
        return;
      }
      if (data?.error) {
        setSubmitError(data.error);
        return;
      }
      // Deliberately NOT clearCart() here. The cart is only safe to empty once
      // the order is actually confirmed — clearing it on the way out to Stripe
      // means an abandoned checkout returns to an empty cart with the stock
      // still held, and no way for the shopper to see what happened.
      // OrderConfirmationPage clears it once the order really lands.
      setPendingOrder({ token: data.order_token, url: data.url });
      window.location.href = data.url;
    } catch (e) {
      // supabase-js only puts the generic HTTP status in e.message for a
      // non-2xx edge function response — the actual { error } JSON body is
      // on e.context (a Response), so unwrap that for a useful message.
      let message = e.message || 'Checkout failed — please try again.';
      if (e.context?.json) {
        try {
          const body = await e.context.json();
          if (body?.error) message = body.error;
        } catch { /* fall back to the generic message */ }
      }
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sv-container sv-page-top sv-page-bottom" style={{ maxWidth: 560 }}>
      <h1 style={{ fontSize: '1.6rem', fontWeight: 900, marginBottom: 24 }}>Checkout</h1>

      <PendingOrderBanner />

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {!user && (
          <div className="sv-card" style={{ padding: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Contact info</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input className="sv-input" type="email" placeholder="Email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} required />
              <input className="sv-input" placeholder="Name (optional)" value={guestName} onChange={(e) => setGuestName(e.target.value)} />
            </div>
          </div>
        )}

        <div className="sv-card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Fulfillment</div>
          {/* With pickup paused there is nothing to choose, so the page says
              what happens instead of offering one option as a question. */}
          {PICKUP_ENABLED ? (
            <div style={{ display: 'flex', gap: 16, marginBottom: fulfillment === 'ship' ? 14 : 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={fulfillment === 'pickup'} onChange={() => setFulfillment('pickup')} />
                Local pickup
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={fulfillment === 'ship'} onChange={() => setFulfillment('ship')} />
                Ship to me
              </label>
            </div>
          ) : (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 14 }}>
              Shipped to you. {SHIP_WINDOW}. Local pickup is paused for now.
            </div>
          )}

          {fulfillment === 'pickup' && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              We'll email you when your order is ready for pickup. Nothing to pay for shipping.
            </p>
          )}

          {/* The rules, before they enter an address — not after a rate comes back. */}
          {fulfillment === 'ship' && (
            <div style={{
              fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.6,
              border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', marginBottom: 12,
            }}>
              <div>{insuranceLine}</div>
              <div>{signatureLine}</div>
              <div style={{ marginTop: 4 }}>
                Postage is the carrier's own rate with no handling fee added.{' '}
                <Link to="/shipping" style={{ fontWeight: 700 }}>Shipping and pricing</Link>
              </div>
            </div>
          )}

          {fulfillment === 'ship' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input className="sv-input" placeholder="Full name" value={address.name} onChange={(e) => { setAddress({ ...address, name: e.target.value }); clearQuote(); }} required />
              <input className="sv-input" placeholder="Street address" value={address.street1} onChange={(e) => { setAddress({ ...address, street1: e.target.value }); clearQuote(); }} required />
              <input className="sv-input" placeholder="Apt / suite (optional)" value={address.street2} onChange={(e) => { setAddress({ ...address, street2: e.target.value }); clearQuote(); }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="sv-input" placeholder="City" value={address.city} onChange={(e) => { setAddress({ ...address, city: e.target.value }); clearQuote(); }} required />
                <input className="sv-input" style={{ width: 90 }} placeholder="State" value={address.state} onChange={(e) => { setAddress({ ...address, state: e.target.value }); clearQuote(); }} required />
                <input className="sv-input" style={{ width: 110 }} placeholder="ZIP" value={address.zip} onChange={(e) => { setAddress({ ...address, zip: e.target.value }); clearQuote(); }} required />
              </div>

              {!quote && (
                <button
                  type="button"
                  onClick={getRate}
                  disabled={!addressComplete || ratePending}
                  className="sv-btn-outline"
                  style={{ marginTop: 4 }}
                >
                  {ratePending ? 'Checking rates…' : 'Get shipping options'}
                </button>
              )}
              {rateError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>{rateError}</div>}

              {quote && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  {quote.options.map((o) => {
                    const active = o.shippo_rate_id === selectedRateId;
                    return (
                      <label
                        key={o.shippo_rate_id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                          padding: '10px 12px', borderRadius: 9,
                          // Shorthand on both sides so the active override can't
                          // half-apply (see the CSS shorthand rule in CLAUDE.md).
                          border: active ? '2px solid var(--accent)' : '2px solid var(--border)',
                        }}
                      >
                        <input
                          type="radio"
                          name="shipopt"
                          checked={active}
                          onChange={() => setSelectedRateId(o.shippo_rate_id)}
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>
                              {o.carrier} {o.service}
                            </span>
                            {o.cheapest && (
                              <span style={{
                                fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase',
                                letterSpacing: '0.04em', padding: '2px 6px', borderRadius: 4,
                                background: 'var(--accent)', color: '#fff',
                              }}>Lowest cost</span>
                            )}
                            {o.fastest && !o.cheapest && (
                              <span style={{
                                fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase',
                                letterSpacing: '0.04em', padding: '2px 6px', borderRadius: 4,
                                border: '1px solid var(--border)', color: 'var(--text-muted)',
                              }}>Fastest</span>
                            )}
                          </span>
                          <span style={{ display: 'block', fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 2 }}>
                            {o.estimated_days
                              ? `Est. ${o.estimated_days} business day${o.estimated_days === 1 ? '' : 's'}`
                              : 'Delivery estimate unavailable'}
                          </span>
                        </span>
                        <span style={{ fontWeight: 800, fontSize: '0.92rem' }}>{money(o.total_cents)}</span>
                      </label>
                    );
                  })}

                  {quote.insurance_cents > 0 && selected && (
                    <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                      {money(selected.postage_cents)} postage + {money(quote.insurance_cents)} insurance
                      for the full value of your order.
                    </div>
                  )}
                  {quote.signature_required && (
                    <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                      Signature required on delivery — someone must be available to sign.
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={getRate}
                    disabled={ratePending}
                    style={{
                      alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0,
                      color: 'var(--text-muted)', fontSize: '0.74rem', textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    {ratePending ? 'Refreshing…' : 'Refresh rates'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="sv-card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: 6 }}>
            <span>Subtotal</span><span>{money(subtotalCents)}</span>
          </div>
          {fulfillment === 'ship' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: 6, color: 'var(--text-muted)' }}>
              <span>Shipping{selected ? ` — ${selected.carrier} ${selected.service}` : ''}</span>
              <span>{selected ? money(selected.total_cents) : '—'}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1.05rem', marginTop: 8 }}>
            <span>Total</span><span>{money(totalCents)}</span>
          </div>
        </div>

        {/* Said before they commit, not after. The next screen is Stripe, where
            the amount looks exactly like a charge — someone who wasn't told
            here will read the pending line on their statement as one. */}
        <HoldNotice amountLabel={money(totalCents)} />

        {submitError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>{submitError}</div>}

        <button type="submit" className="sv-btn" disabled={submitting} style={{ padding: 14 }}>
          {submitting ? 'Redirecting to payment…' : 'Continue to payment'}
        </button>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: -8 }}>
          You won&rsquo;t be charged today.
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: -12 }}>
          Having trouble?{' '}
          <a
            href="https://instagram.com/trainercenter.pokemon"
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--text)' }}
          >
            DM us @trainercenter.pokemon
          </a>
        </div>
      </form>
    </div>
  );
}
